import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { collection, onSnapshot, orderBy, query as fsQuery } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { apiGet, apiPost, apiPut, apiDelete } from '../api';
import { firebaseDb } from '../firebase';
import { backendOf, usesTempCleanup, FUSION } from '../utils/connectionRouting';

const CONNECTIONS_KEY   = 'fq-connections';
const ACTIVE_CONN_KEY   = 'fq-active-connection';
const LEGACY_CONFIG_KEY = 'bip-sql-config'; // original legacy key (migrated on first load)

// One-time migration: copy data from old 'bip-sql-*' keys to new 'fq-*' keys
;[
  ['bip-sql-connections',       'fq-connections'],
  ['bip-sql-active-connection', 'fq-active-connection'],
].forEach(([oldKey, newKey]) => {
  try {
    const v = localStorage.getItem(oldKey);
    if (v !== null && localStorage.getItem(newKey) === null) localStorage.setItem(newKey, v);
    localStorage.removeItem(oldKey);
  } catch { /* ignore */ }
});

// ---------------------------------------------------------------------------
// Load / migrate helpers (localStorage)
// ---------------------------------------------------------------------------
function loadConnectionsFromStorage() {
  try {
    const raw = localStorage.getItem(CONNECTIONS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch { /* ignore */ }

  // Migrate legacy single-connection storage
  try {
    const legacy = localStorage.getItem(LEGACY_CONFIG_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy);
      if (parsed.baseUrl || parsed.username) {
        return [{
          id: '1',
          name: 'Default',
          baseUrl:  parsed.baseUrl  || '',
          username: parsed.username || '',
          password: parsed.password || '',
          createdAt: new Date().toISOString(),
        }];
      }
    }
  } catch { /* ignore */ }

  return [];
}

function loadActiveId(connections) {
  try {
    const saved = localStorage.getItem(ACTIVE_CONN_KEY);
    if (saved && connections.find(c => c.id === saved)) return saved;
  } catch { /* ignore */ }
  return connections[0]?.id || null;
}

const ConfigContext = createContext(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export function ConfigProvider({ children, serverConfig = {} }) {
  const { user } = useAuth();
  const saasAuthEnabled = serverConfig?.saasAuthEnabled ?? false;
  const cloudMode = saasAuthEnabled && !!user;

  const [connections, setConnections]       = useState(() => cloudMode ? [] : loadConnectionsFromStorage());
  const [activeConnectionId, setActiveConnectionId] = useState(() =>
    cloudMode ? null : loadActiveId(loadConnectionsFromStorage())
  );
  const [cloudLoaded, setCloudLoaded]       = useState(false);
  const migratingRef                        = useRef(false);
  // Tracks the latest fetch so stale responses from concurrent calls are discarded
  const fetchSeqRef                         = useRef(0);

  // ---------------------------------------------------------------------------
  // Cloud mode: real-time Firestore listener + server fetch for decryption
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!cloudMode || !user?.uid) return;

    // Reset state when user changes
    setConnections([]);
    setCloudLoaded(false);
    migratingRef.current = false;
    fetchSeqRef.current = 0;

    const restoreActiveConnection = (remote) => {
      const savedActiveId = localStorage.getItem(ACTIVE_CONN_KEY);
      const validActive   = remote.find(c => c.id === savedActiveId);
      setActiveConnectionId(validActive ? savedActiveId : remote[0]?.id || null);
    };

    const fetchRemoteConnections = async ({ allowMigration = false, restoreActive = false } = {}) => {
      const seq = ++fetchSeqRef.current;
      const remote = await apiGet('/connections');
      if (seq !== fetchSeqRef.current) return null; // superseded by a newer fetch

      if (allowMigration && remote.length === 0 && !migratingRef.current) {
        // First load and cloud is empty — migrate from localStorage if needed
        const local = loadConnectionsFromStorage();
        if (local.length > 0) {
          migratingRef.current = true;
          for (const conn of local) {
            try {
              await apiPost('/connections', {
                name:     conn.name     || 'Default',
                baseUrl:  conn.baseUrl  || '',
                username: conn.username || '',
                password: conn.password || '',
              });
            } catch (e) {
              console.warn('Migration: failed to migrate connection', conn.name, e);
            }
          }
          try {
            localStorage.removeItem(CONNECTIONS_KEY);
            localStorage.removeItem(ACTIVE_CONN_KEY);
            localStorage.removeItem(LEGACY_CONFIG_KEY);
          } catch {}
          return null; // onSnapshot or the next explicit fetch will load migrated rows
        }
      }

      setConnections(remote);
      if (restoreActive) restoreActiveConnection(remote);
      return remote;
    };

    fetchRemoteConnections({ allowMigration: true, restoreActive: true })
      .catch((e) => console.error('Failed to fetch connections from server:', e))
      .finally(() => setCloudLoaded(true));

    let isFirstSnapshot = true;

    const q = fsQuery(
      collection(firebaseDb, 'userConnections', user.uid, 'connections'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, async () => {
      // Each Firestore change re-fetches from server to get decrypted passwords.
      // The fetch sequence number guards against stale out-of-order responses.
      try {
        await fetchRemoteConnections({
          allowMigration: isFirstSnapshot,
          restoreActive: isFirstSnapshot,
        });
      } catch (e) {
        console.error('Failed to fetch connections from server:', e);
      } finally {
        if (isFirstSnapshot) {
          setCloudLoaded(true);
          isFirstSnapshot = false;
        }
      }
    }, (err) => {
      console.error('Firestore connections listener error:', err);
      fetchRemoteConnections({ restoreActive: true })
        .catch((e) => console.error('Failed to fetch connections after listener error:', e))
        .finally(() => setCloudLoaded(true));
    });

    return () => unsubscribe();
  }, [cloudMode, user?.uid]); // intentional: re-subscribe only when user or mode changes

  // ---------------------------------------------------------------------------
  // localStorage mode: persist whenever connections change
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (cloudMode) return;
    try { localStorage.setItem(CONNECTIONS_KEY, JSON.stringify(connections)); } catch {}
  }, [connections, cloudMode]);

  useEffect(() => {
    if (cloudMode) return;
    if (activeConnectionId) {
      try { localStorage.setItem(ACTIVE_CONN_KEY, activeConnectionId); } catch {}
    }
  }, [activeConnectionId, cloudMode]);

  // Derive the credential bundle spread into every /bip/* request.
  // `connectionType` is what the server dispatches on; connections saved before
  // WMS support existed have no type and default to Fusion/BIP.
  const activeConnection = connections.find(c => c.id === activeConnectionId) || connections[0] || null;
  const config = {
    baseUrl:        activeConnection?.baseUrl  || '',
    username:       activeConnection?.username || '',
    password:       activeConnection?.password || '',
    connectionType: backendOf(activeConnection),
    env:            activeConnection?.env      || '',
    facility:       activeConnection?.facility || '',
  };

  // Back-compat: setConfig updates the currently active connection's fields
  const setConfig = useCallback((updater) => {
    const fields = typeof updater === 'function' ? updater(activeConnection || {}) : updater;
    if (cloudMode && activeConnectionId) {
      apiPut(`/connections/${activeConnectionId}`, fields).catch(console.error);
    }
    setConnections(prev => prev.map(c =>
      c.id === activeConnectionId ? { ...c, ...fields } : c
    ));
  }, [activeConnectionId, cloudMode, activeConnection]);

  // ── CRUD ─────────────────────────────────────────────────────────────────
  const addConnection = useCallback(async (fields = {}) => {
    if (cloudMode) {
      try {
        const created = await apiPost('/connections', {
          name:     fields.name     || 'New Connection',
          type:     fields.type     || FUSION,
          baseUrl:  fields.baseUrl  || '',
          env:      fields.env      || '',
          facility: fields.facility || '',
          username: fields.username || '',
          password: fields.password || '',
        });
        if (created) {
          // Do not depend solely on Firestore listeners; they can be delayed or blocked.
          setConnections(prev => prev.some(c => c.id === created.id) ? prev : [...prev, created]);
          setActiveConnectionId(created.id);
          return created.id;
        }
      } catch (e) {
        console.error('addConnection (cloud):', e);
      }
      return null;
    }
    // localStorage mode
    const id = String(Date.now());
    setConnections(prev => [...prev, {
      id,
      name:      'New Connection',
      type:      FUSION,
      baseUrl:   '',
      env:       '',
      facility:  '',
      username:  '',
      password:  '',
      createdAt: new Date().toISOString(),
      ...fields,
    }]);
    setActiveConnectionId(id);
    return id;
  }, [cloudMode]);

  const updateConnection = useCallback((id, fields) => {
    if (cloudMode) {
      apiPut(`/connections/${id}`, fields).catch(console.error);
      // onSnapshot will update state; optimistically update locally too for responsiveness
    }
    setConnections(prev => prev.map(c => c.id === id ? { ...c, ...fields } : c));
  }, [cloudMode]);

  const deleteConnection = useCallback((id) => {
    if (cloudMode) {
      apiDelete(`/connections/${id}`).catch(console.error);
    }
    setConnections(prev => {
      const next = prev.filter(c => c.id !== id);
      if (id === activeConnectionId) {
        setActiveConnectionId(next[0]?.id || null);
      }
      return next;
    });
  }, [activeConnectionId, cloudMode]);

  const activateConnection = useCallback((id) => {
    setActiveConnectionId(id);
    if (!cloudMode) {
      try { localStorage.setItem(ACTIVE_CONN_KEY, id); } catch {}
    }
  }, [cloudMode]);

  // ── Connection health statuses ────────────────────────────────────────────
  // { [id]: 'testing' | 'ok' | 'fail' | null }
  // Shared across all UI so sidebar, toolbar, and bottom bar stay in sync.
  const [connectionStatuses, setConnectionStatuses] = useState({});
  const testedIdsRef = useRef(new Set());
  // Connection ids we've already kicked off a background cleanup for in this
  // browser session — guards against double-firing within the same tab even
  // if testConnection is called repeatedly.
  const cleanedThisSessionRef = useRef(new Set());

  const CLEANUP_THROTTLE_MS = 24 * 60 * 60 * 1000; // 24h

  // Background sweep of orphaned _Tmp objects in the user's Oracle BIP_TEMP
  // folder. Throttled per connection: at most once per 24h (server records
  // lastCleanupAt; client also dedupes within a session). Silent — failures
  // are housekeeping noise, not user-facing.
  const maybeRunCleanup = useCallback((conn) => {
    if (!conn?.id || !conn.baseUrl || !conn.username) return;
    // BIP-only: WMS connections create no temp catalog objects.
    if (!usesTempCleanup(conn)) return;
    if (cleanedThisSessionRef.current.has(conn.id)) return;

    const lastTs = conn.lastCleanupAt
      ? (conn.lastCleanupAt instanceof Date
          ? conn.lastCleanupAt.getTime()
          : new Date(conn.lastCleanupAt).getTime())
      : 0;
    if (lastTs && Date.now() - lastTs < CLEANUP_THROTTLE_MS) return;

    cleanedThisSessionRef.current.add(conn.id);
    apiPost('/bip/cleanup-temp', {
      baseUrl:      conn.baseUrl,
      username:     conn.username,
      password:     conn.password,
      connectionId: conn.id,
    }).catch(() => { /* silent — server logs the real cause */ });
  }, []);

  const testConnection = useCallback((conn) => {
    if (!conn?.id) return;
    if (!conn.baseUrl || !conn.username) {
      setConnectionStatuses(s => ({ ...s, [conn.id]: null }));
      return;
    }
    setConnectionStatuses(s => ({ ...s, [conn.id]: 'testing' }));
    apiPost('/bip/test-connection', {
      baseUrl: conn.baseUrl, username: conn.username, password: conn.password,
      connectionType: backendOf(conn), env: conn.env || '',
    })
      .then(r => {
        setConnectionStatuses(s => ({ ...s, [conn.id]: r.success ? 'ok' : 'fail' }));
        if (r.success) maybeRunCleanup(conn);
      })
      .catch(()  => setConnectionStatuses(s => ({ ...s, [conn.id]: 'fail' })));
  }, [maybeRunCleanup]);

  // Test each connection once when it first appears; re-test after explicit save
  useEffect(() => {
    connections.forEach(c => {
      if (!testedIdsRef.current.has(c.id)) {
        testedIdsRef.current.add(c.id);
        testConnection(c);
      }
    });
  }, [connections, testConnection]);

  // Reset tested set when user changes so connections are re-tested on login
  useEffect(() => {
    testedIdsRef.current = new Set();
    cleanedThisSessionRef.current = new Set();
    setConnectionStatuses({});
  }, [user?.uid]);

  return (
    <ConfigContext.Provider value={{
      // Legacy single-config interface (unchanged)
      config,
      setConfig,
      // Multi-connection interface
      connections,
      activeConnectionId,
      activeConnection,
      addConnection,
      updateConnection,
      deleteConnection,
      activateConnection,
      // Cloud sync state
      cloudMode,
      cloudLoaded,
      // Connection health
      connectionStatuses,
      testConnection,
    }}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error('useConfig must be used inside ConfigProvider');
  return ctx;
}
